import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Wrapper, Menu, openMenu, closeMenu } from 'react-aria-menubutton';
import styled, { css } from 'styled-components';
import { v4 } from 'uuid';
import { filterProps, useMediaSize } from '../tools/utils';
import { CloseButton, CloseButtonSpacer } from '../tools/components';
import DropdownMenuContext from './DropdownMenuContext';
import MenuItemLink from './DropdownMenuItem/MenuItemLink';
import MenuItemButton from './DropdownMenuItem/MenuItemButton';
import Trigger from './Trigger';
import SubMenu from './DropdownMenuItem/SubMenu';
import { useInternalDropdownContext, } from './use-dropdown-context';
const SM_CARET_WIDTH = 10;
const SM_CARET_BORDER = '0 10px 10px 10px';
const LG_CARET_WIDTH = 16;
const LG_CARET_BORDER = '0 16px 16px 16px';
const StyledWrapper = styled(Wrapper) `
  position: relative;
  display: inline-block;
`;
const MenuStyles = css `
  ${({ isMounted, isFlush, topPos, shouldAlignLeft, triggerLeftPos, triggerRightOffset, smallCaret, rightAlignCaret, caretPos, smDownCaretPos, caretOffsetX, menuOffsetX, $scrollBarWidth, theme: { media, colors }, }) => {
    const CARET_WIDTH = smallCaret ? SM_CARET_WIDTH : LG_CARET_WIDTH;
    const CARET_BORDER_WIDTH = smallCaret ? SM_CARET_BORDER : LG_CARET_BORDER;
    const STD_CHEVRON_WIDTH = 12;
    const RIGHT_ALIGNED_PAD = 7;
    return `
      position: absolute;
      background-color: ${colors.white};
      color: black;
      min-width: 15em;
      box-shadow: 0.2em 0.2em 0.2em rgba(0, 0, 0, 0.25);
      border: 1px solid ${colors['slate-3']};
      border-top: ${isFlush ? 'none' : `1px solid ${colors['slate-3']}`};
      z-index: 2;
      top: ${topPos}px;
      ${shouldAlignLeft
        ? `left: ${menuOffsetX}px;`
        : `right: ${-menuOffsetX}px;`}
      /**
       * Override the default menu positioning to make room for the caret if right aligned.
       */
      ${rightAlignCaret && !shouldAlignLeft
        ? `right: -${RIGHT_ALIGNED_PAD -
            menuOffsetX +
            // 1px for the border on one side
            (CARET_WIDTH - 1) -
            STD_CHEVRON_WIDTH / 2}px;`
        : ''}

      ${media.down('sm')} {
        ${shouldAlignLeft
        ? `left: -${triggerLeftPos}px;`
        : `right: -${triggerRightOffset}px;`}
        width: calc(100vw - ${$scrollBarWidth}px);
      }

      /**
       * This is to prevent the menu from mounting off of the trigger then moving on the first alignment.
       * It still happens this way, but it's not visible to the user until it's aligned.
       */
      opacity: ${isMounted ? '1' : '0'};

      ::before,
      ::after {
        content: ' ';
        display: block;
        position: absolute;
        width: 0;
        border-style: solid;
        pointer-events: none;
        border-width: ${CARET_BORDER_WIDTH};

        ${shouldAlignLeft
        ? `left: ${caretPos + caretOffsetX}px;`
        : `right: ${caretPos - caretOffsetX}px;`}
        ${rightAlignCaret && !shouldAlignLeft
        ? // 1 px for the border on one side
            `right: ${1 + RIGHT_ALIGNED_PAD}px;`
        : ''}

        ${media.down('sm')} {
          ${shouldAlignLeft
        ? `left: ${smDownCaretPos + caretOffsetX}px;`
        : `right: ${smDownCaretPos - caretOffsetX}px;`}
          ${rightAlignCaret && !shouldAlignLeft
        ? `right: ${(triggerRightOffset || 0) -
            // 1 px for the border on one side
            (CARET_WIDTH - 1) +
            STD_CHEVRON_WIDTH / 2}px;`
        : ''}
        }
      }

      /**
       * This is the main caret. It is one pixel lower than its dimensions and has
       * a higher z-index so that it overlays and covers the border of its parent.
       */
      ::after {
        top: -${CARET_WIDTH - 1}px;
        border-color: transparent transparent white transparent;
        z-index: 10;
      }

      /**
       * This provides the border for the caret. It's one pixel higher and has a
       * default z-index so that it is both above and behind the main caret.
       */
      ::before {
        top: -${CARET_WIDTH}px;
        border-color: ${`transparent transparent ${colors['slate-3']} transparent`};
      }
    `;
}}
`;
const FilteredMenu = filterProps(Menu, [
    'isMounted',
    'isFlush',
    'topPos',
    'shouldAlignLeft',
    'triggerLeftPos',
    'triggerRightOffset',
    'smallCaret',
    'rightAlignCaret',
    'caretPos',
    'smDownCaretPos',
    'caretOffsetX',
    'menuOffsetX',
]);
const StyledMenu = styled(FilteredMenu) `
  ${MenuStyles}
`;
export const MenuContainer = ({ className, isOpen, topPos = 50, smallCaret = false, rightAlignCaret = false, isFlush = false, caretOffsetX = 0, menuOffsetX = 0, variant = 'menu', alignSelf = 'auto', showCloseButton = false, openOnMount, context, wrapperId, children, }) => {
    const containerRef = useRef(null);
    const [caretPos, setCaretPos] = useState(0);
    const [shouldAlignLeft, setShouldAlignLeft] = useState(false);
    const [smDownCaretPos, setSmDownCaretPos] = useState(0);
    const [triggerLeftPos, setTriggerLeftPos] = useState(0);
    const [triggerRightOffset, setTriggerRightOffset] = useState(0);
    const [isMounted, setMounted] = useState(false);
    const isSmDown = useMediaSize('SmDown');
    const isMdOnly = useMediaSize('MdOnly');
    const isLgUp = useMediaSize('LgUp');
    useInternalDropdownContext({ context, wrapperId });
    useEffect(() => {
        if (openOnMount && wrapperId)
            openMenu(wrapperId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!containerRef.current || !isOpen)
            return;
        const triggerBoundRect = containerRef.current.getBoundingClientRect();
        const localTriggerLeftPos = triggerBoundRect.left;
        const triggerRightPos = triggerBoundRect.right;
        const triggerCenterPos = (localTriggerLeftPos + triggerRightPos) / 2;
        const menuBoundRect = containerRef.current.getBoundingClientRect();
        const menuLeftPos = menuBoundRect.left;
        const menuRightPos = menuBoundRect.right;
        const windowMidpoint = window.innerWidth / 2;
        const localShouldAlignLeft = alignSelf === 'auto'
            ? triggerCenterPos < windowMidpoint
            : alignSelf === 'left';
        // subtracting 1px because caret is shifted down 1px to create a border effect
        const caretWidth = smallCaret ? SM_CARET_WIDTH : LG_CARET_WIDTH - 1;
        let localCaretPos = 0;
        if (localShouldAlignLeft) {
            localCaretPos = triggerCenterPos - menuLeftPos - caretWidth;
        }
        else {
            localCaretPos = menuRightPos - triggerCenterPos - caretWidth;
        }
        let localSmDownCaretPos = 0;
        if (localShouldAlignLeft) {
            localSmDownCaretPos = triggerCenterPos - caretWidth;
        }
        else {
            localSmDownCaretPos = window.innerWidth - triggerCenterPos - caretWidth;
        }
        setShouldAlignLeft(localShouldAlignLeft);
        setTriggerLeftPos(localTriggerLeftPos);
        setTriggerRightOffset(window.innerWidth - triggerRightPos);
        setCaretPos(localCaretPos);
        setSmDownCaretPos(localSmDownCaretPos);
        setMounted(true);
        /**
         * We don't need to respond internally to the `isMdOnly` or `isLgUp`
         * breakpoints, but implementing applications may change their trigger
         * at different breakpoints. If this happens, we need to re-calculate
         * our dimensions and positioning in response to that.
         */
    }, [isOpen, smallCaret, alignSelf, isSmDown, isMdOnly, isLgUp, children]);
    const renderCloseButton = useMemo(() => showCloseButton && variant === 'custom' && !!wrapperId, [showCloseButton, variant, wrapperId]);
    const scrollBarWidth = window.innerWidth - document.body.clientWidth;
    const menuProps = {
        topPos,
        caretPos,
        shouldAlignLeft,
        smallCaret,
        rightAlignCaret,
        isFlush,
        triggerLeftPos,
        triggerRightOffset,
        smDownCaretPos,
        caretOffsetX,
        menuOffsetX,
        isMounted,
        $scrollBarWidth: scrollBarWidth !== null && scrollBarWidth !== void 0 ? scrollBarWidth : 0,
    };
    return (React.createElement("div", { ref: containerRef },
        React.createElement(StyledMenu, Object.assign({ className: className }, menuProps, { role: variant === 'menu' ? 'menu' : undefined, "data-testid": "mui-dropdown-menu" }),
            React.createElement(React.Fragment, null,
                renderCloseButton && React.createElement(CloseButtonSpacer, null),
                children,
                renderCloseButton && (React.createElement(CloseButton, { onClick: () => {
                        // wrapperId must be defined for renderCloseButton to be true
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        closeMenu(wrapperId, { focusButton: true });
                    }, "data-testid": "mui-dropdown-menu-close-button" }))))));
};
/**
 * @deprecated
 * DropdownMenuDEPRECATED has been deprecated
 * and _will be removed_ in the v25 release of metronome-ui.
 * Please use the DropdownMenu component instead.
 */
class DropdownMenuDEPRECATED extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
            wrapperId: v4(),
        };
    }
    render() {
        const { children, className, topPos, smallCaret, rightAlignCaret, isFlush, caretOffsetX, menuOffsetX, variant, alignSelf, showCloseButton, context, openOnMount, onMenuToggle = (isOpen) => null, } = this.props;
        const { isOpen, wrapperId } = this.state;
        this.componentDidUpdate = (_, prevState) => {
            if (isOpen !== prevState.isOpen) {
                onMenuToggle(isOpen);
            }
        };
        return (React.createElement(DropdownMenuContext.Provider, { value: { wrapperId, isOpen } },
            React.createElement(StyledWrapper, { id: wrapperId, className: className, onMenuToggle: (toggleUpdate) => {
                    this.setState({ isOpen: toggleUpdate.isOpen });
                }, closeOnSelection: false }, React.Children.map(children, (child, i) => {
                return React.cloneElement(child, Object.assign({ isOpen,
                    topPos,
                    smallCaret,
                    rightAlignCaret,
                    isFlush,
                    caretOffsetX,
                    menuOffsetX,
                    alignSelf,
                    showCloseButton,
                    openOnMount,
                    context,
                    wrapperId }, (child.props.variant
                    ? {}
                    : { variant })));
            }))));
    }
}
DropdownMenuDEPRECATED.Trigger = Trigger;
DropdownMenuDEPRECATED.MenuContainer = MenuContainer;
DropdownMenuDEPRECATED.MenuItemLink = MenuItemLink;
DropdownMenuDEPRECATED.MenuItemButton = MenuItemButton;
DropdownMenuDEPRECATED.SubMenu = SubMenu;
export { DropdownMenuDEPRECATED };
//# sourceMappingURL=DropdownMenu.js.map